import React from 'react';
import { useTheme } from '@components/theme';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';
import RELATED_BOARDS_QUERY from '@graphql/VendorVariant/queries/get-related-boards.graphql';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SrcSetPicture } from '@components/shared/Picture';
import AspectRatioBox from '@components/shared/AspectRatioBox/AspectRatioBox';
import { SrcSet } from '@lib/image';
import SwiperCore, { Navigation } from 'swiper';
import CarouselNavigator from '@components/shared/carousels/CarouselNavigator/CarouselNavigator';
import DeferredImage from '@components/shared/images/atoms/Image/DeferredImage';
import { useMinBreakpoint } from '@hooks/useMediaQuery/useMediaQuery';
import breakpoints from '@constants/breakpoints';
import styles from './FeaturedDesigns.module.scss';

const PAGE_SIZE = 5;

SwiperCore.use([Navigation]);

interface IFeaturedDesignsProps {
  vendorVariantIds: number[];
  analyticsListId?: string;
}

interface IRelatedBoards {
  boardsRelatedToProduct: any[];
}

interface IDiscoverBoardItem {
  id: string;
  subStyles?: {
    name: string;
  }[];
  designer?: {
    firstName: string;
    profileUri: string;
  };
  room?: {
    roomType?: {
      type: string;
    };
  };
  mainBoardAsset?: {
    asset?: {
      url: string;
    };
  };
  boardProductCount?: number;
  publicName?: string;
  roomType?: {
    type: string;
  };
}

function getSrcSetForViewport(url: string) {
  const mobileSrcSets: Record<string, SrcSet> = {};
  ['webp', 'jpeg'].forEach((format) => {
    mobileSrcSets[format] =
      new SrcSet({
        sizes: new Map([
          [SrcSet.DEFAULT, [1920, 1920]],
          [`${breakpoints['large-phone']}`, [640, 640]],
          [`${breakpoints['large-tablet']}`, [960, 960]],
        ]),
        url,
        props: {
          fit: 'fit-in',
          quality: 99,
          format,
        }
      });
  });

  return mobileSrcSets;
}

function getLink(item:IDiscoverBoardItem, analyticsListId: string) {
  const analyticsLink = analyticsListId ? `#list=${analyticsListId}` : '';
  return `/interior-design-ideas/design-board/${item.id}${analyticsLink}`;
}

export default function FeaturedDesigns({
  vendorVariantIds,
  analyticsListId = ''
}: IFeaturedDesignsProps) {
  const theme = useTheme();

  const isDesktop = useMinBreakpoint(breakpoints.desktop);

  const { data } = useQuery<IRelatedBoards>(
    RELATED_BOARDS_QUERY,
    {
      variables: {
        pageSize: PAGE_SIZE,
        ids: vendorVariantIds,
      },
      ssr: false,
    },
  );

  const relatedBoards = data?.boardsRelatedToProduct || [];

  if (!relatedBoards.length) return null;

  const aspectRatio = isDesktop ? 0.56 : 1;

  if (relatedBoards.length < 2) {
    return null;
  }

  return (
    <section className={styles.Container}>
      <h1 className={classNames(theme.styles.Title1, styles.Title)}>
        Make it your own
      </h1>
      <p className={classNames(theme.styles.Body, styles.Subtitle)}>
        See how Havenly Designers have styled this piece for real customers
      </p>

      <Swiper
        centeredSlides
        className={styles.swiper}
        slidesPerView={1}
        loop
        navigation={{
          nextEl: '.swiper-next',
          prevEl: '.swiper-previous',
        }}
      >
        {
          relatedBoards.map((item) => {
            return (
              <SwiperSlide className={styles.Board} key={item.id}>
                <a href={getLink(item, analyticsListId)}>
                  <AspectRatioBox className={styles.ImageWrapper} aspectRatio={aspectRatio}>
                    <SrcSetPicture
                      srcSets={getSrcSetForViewport(item.mainBoardAsset.asset.url)}
                      defaultWidth={1920}
                      render={({ imgProps }) => (
                        <DeferredImage
                          className={styles.Image}
                          imgProps={{
                            ...imgProps,
                            alt: 'Room'
                          }}
                        />
                      )}
                    />
                  </AspectRatioBox>
                </a>
              </SwiperSlide>
            );
          })
        }
      </Swiper>

      <CarouselNavigator className={styles.Pagination} navNext="swiper-next" navPrev="swiper-previous" />
    </section>
  );
}
